import { AppDataEnum } from '@kira-dancer/nuxt-common/enums'
import { keyBy, get } from 'lodash'
import { TopWinnerSectionTabsEnum } from '~/enums'
import type { TopWinningResponse } from '~/types/top-winner'

export function useTopDailyWinners() {
  const { fetchTopWinning } = useLobbyGames()
  const { allGames } = useFetchGames()

  const fetchDataFetchTopWinning = async () => {
    const cachedData = cacheManager.get(AppDataEnum.TopWinning)
    if (cachedData) {
      return cachedData
    }
    try {
      const data = await fetchTopWinning('', TopWinnerSectionTabsEnum.Daily)
      cacheManager.set(AppDataEnum.TopWinning, data, 360)
      return data
    } catch {
      return []
    }
  }

  const { data: rawData } = useAsyncData(
    AppDataEnum.TopWinning,
    fetchDataFetchTopWinning,
  )

  const gameTopWinners = computed(() => {
    if (!rawData.value?.length) return []
    const response = rawData.value[0] as unknown as TopWinningResponse
    const newData = response?.data
    if (!newData) return []

    const summary = get(newData, 'summary')
    const topBettedGames = get(newData, 'top_betted_games')

    if (!Array.isArray(summary) || !topBettedGames) return []

    const gameMap = keyBy(allGames?.value, (game) => game?.gameId)

    const dataGames = summary
      .map((item) => {
        const username = get(item, 'username')
        if (!username) return null

        const games = get(topBettedGames, username) ?? []
        const formattedGames = games
          .map((game) => {
            const gameId = get(game, 'game_id')
            if (!gameId) {
              return null
            }

            return {
              gameId: (gameId as string)?.replace('_', ''),
              amount: get(game, 'count') ?? 0,
            }
          })
          .filter(Boolean) // Remove null values

        if (!formattedGames.length) {
          return null
        }

        return {
          username,
          amount: get(item, 'amount') ?? 0,
          games: formattedGames,
        }
      })
      .filter(Boolean) // Remove null values

    return dataGames.map((winner) => {
      const topBettedGame = winner?.games.find((game) => {
        const mappedGame = get(gameMap, game?.gameId ?? '')

        // Ensure mappedGame is an object before accessing properties
        if (
          typeof mappedGame === 'object' &&
          mappedGame !== null &&
          'gameId' in mappedGame
        ) {
          return mappedGame.gameId === game?.gameId
        }

        return false
      })

      const gameItem = gameMap[topBettedGame?.gameId ?? '']
      const totalAmount = winner?.games.reduce(
        (total, item) => total + (item?.amount || 0),
        0,
      )
      const gameImage = get(gameItem, 'img.thumbnail.url') || ''
      const gameName = get(gameItem, 'gameName') || ''

      return {
        username: winner?.username ?? '',
        avatar: 1,
        amount: totalAmount,
        game_img: {
          cover: { url: gameImage },
          thumbnail: { url: gameImage },
        },
        game_title: gameName,
      }
    })
  })

  return {
    gameTopWinners,
  }
}
